import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { GuideSummary, Hub, PostSummary } from '@/types'

import Link from 'next/link'

import {
    ArrowRight,
    BookOpenCheck,
    FlaskConical,
    Search,
    Stethoscope
} from 'lucide-react'

import { Seo } from '@/components/generals/seo'
import { JsonLd } from '@/components/generals/json-ld'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { HubCard } from '@/components/blog/hub-card'
import { FeaturedPostCard, PostCard } from '@/components/blog/post-card'
import { GuideCard } from '@/components/blog/guide-card'
import { Newsletter } from '@/components/blog/newsletter'

import { getHubTheme } from '@/constants/hubs'
import { buildGraph } from '@/lib/structured-data'
import { getGuides, getGuideChapterCounts } from '@/lib/guides'
import {
    getFeaturedPosts,
    getHubPostCounts,
    getHubs,
    getPostCount,
    getRecentPosts
} from '@/lib/posts'

interface HomeProps {
    hubs: Hub[]
    counts: Record<string, number>
    guides: GuideSummary[]
    guideCounts: Record<string, number>
    featured: PostSummary | null
    recent: PostSummary[]
    totalCount: number
}

const STANDARDS = [
    {
        icon: FlaskConical,
        title: 'Evidence-informed',
        text: 'Every article is grounded in current research and reputable health sources, never trends or guesswork.'
    },
    {
        icon: Stethoscope,
        title: 'Sourced and cited',
        text: 'Every claim traces back to published research or guidance from a named health body, so you can check it.'
    },
    {
        icon: BookOpenCheck,
        title: 'Genuinely practical',
        text: 'We translate the science into clear, realistic steps you can actually use in everyday life.'
    }
]

const Home: FC<HomeProps> = ({
    hubs,
    counts,
    guides,
    guideCounts,
    featured,
    recent,
    totalCount
}): ReactNode => {
    const stats = [
        { value: `${totalCount}`, label: 'In-depth articles' },
        { value: '3', label: 'Core health topics' },
        { value: '100%', label: 'Traceable to published sources' }
    ]

    return (
        <Layout>
            <Seo />
            <JsonLd data={buildGraph()} />

            {/* Hero */}
            <section className='border-b border-border bg-background'>
                <Container className='py-16 sm:py-20 lg:py-24'>
                    <div className='mx-auto max-w-3xl text-center'>
                        <span className='inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold text-muted-foreground'>
                            <span className='size-1.5 rounded-full bg-primary' />
                            Independent health knowledge
                        </span>

                        <h1 className='mt-6 text-balance text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl'>
                            Health information that respects your intelligence.
                        </h1>

                        <p className='mx-auto mt-5 max-w-2xl text-pretty text-lg leading-relaxed text-muted-foreground'>
                            HealthPathCore turns current evidence into clear, practical articles across mental
                            health, physical health, and nutrition. Every piece traces back to published
                            research and named health bodies.
                        </p>

                        <form action='/search' method='get' role='search' className='mx-auto mt-8 flex max-w-md items-center gap-2'>
                            <div className='relative flex-1'>
                                <Search className='pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
                                <input
                                    type='search'
                                    name='q'
                                    placeholder='Search articles and topics'
                                    aria-label='Search articles and topics'
                                    className='h-12 w-full rounded-lg border border-border bg-card pl-10 pr-4 text-sm outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ring/30'
                                />
                            </div>
                            <button
                                type='submit'
                                className='inline-flex h-12 items-center gap-1.5 rounded-lg bg-primary px-5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90'
                            >
                                Search
                            </button>
                        </form>

                        <div className='mt-6 flex flex-wrap items-center justify-center gap-2'>
                            <span className='text-sm text-muted-foreground'>Popular:</span>
                            {hubs.map(hub => {
                                const theme = getHubTheme(hub.slug)

                                return (
                                    <Link
                                        key={hub.slug}
                                        href={`/${hub.slug}`}
                                        className='inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-sm font-medium text-foreground transition-colors hover:bg-secondary'
                                    >
                                        <span className={`size-1.5 rounded-full ${theme.dot}`} />
                                        {hub.name}
                                    </Link>
                                )
                            })}
                        </div>
                    </div>

                    <dl className='mx-auto mt-14 grid max-w-3xl grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-3'>
                        {stats.map(stat => (
                            <div key={stat.label} className='flex flex-col bg-card px-6 py-6 text-center'>
                                <dt className='order-2 mt-1 text-sm text-muted-foreground'>{stat.label}</dt>
                                <dd className='order-1 font-heading text-3xl font-bold tracking-tight text-primary'>
                                    {stat.value}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </Container>
            </section>

            {/* Hubs */}
            <section id='topics' className='scroll-mt-20 py-16 sm:py-20'>
                <Container>
                    <div className='max-w-2xl'>
                        <p className='text-sm font-semibold text-primary'>Explore by topic</p>
                        <h2 className='mt-2 text-3xl font-bold tracking-tight sm:text-4xl'>
                            Three hubs, one trusted source
                        </h2>
                        <p className='mt-3 text-muted-foreground'>
                            We focus deeply on the areas that shape daily wellbeing, so every hub stays clear,
                            current, and genuinely useful.
                        </p>
                    </div>

                    <div className='mt-10 grid gap-6 md:grid-cols-3'>
                        {hubs.map(hub => (
                            <HubCard
                                key={hub.slug}
                                slug={hub.slug}
                                name={hub.name}
                                description={hub.description}
                                count={counts[hub.slug] ?? 0}
                            />
                        ))}
                    </div>
                </Container>
            </section>

            {/* Pillar guides: the entry point to each cluster, so they sit above
                the article feed rather than below it. */}
            {guides.length > 0 && (
                <section className='border-t border-border bg-secondary/40 py-16 sm:py-20'>
                    <Container>
                        <div className='flex flex-wrap items-end justify-between gap-4'>
                            <div className='max-w-2xl'>
                                <p className='text-sm font-semibold text-primary'>Start here</p>
                                <h2 className='mt-2 text-3xl font-bold tracking-tight sm:text-4xl'>
                                    Complete guides
                                </h2>
                                <p className='mt-3 text-muted-foreground'>
                                    Each guide covers one topic from the ground up, then points you to every
                                    article underneath it in a sensible reading order.
                                </p>
                            </div>

                            <Link
                                href='/guides'
                                className='inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline'
                            >
                                All guides
                                <ArrowRight className='size-4' />
                            </Link>
                        </div>

                        <div className='mt-10 grid gap-8 lg:grid-cols-3'>
                            {guides.map(guide => (
                                <GuideCard
                                    key={guide.slug}
                                    guide={guide}
                                    chapterCount={guideCounts[guide.id]}
                                />
                            ))}
                        </div>
                    </Container>
                </section>
            )}

            {/* Featured */}
            {featured && (
                <section className='py-16 sm:py-20'>
                    <Container>
                        <div className='mb-8 flex items-end justify-between gap-4'>
                            <h2 className='text-2xl font-bold tracking-tight sm:text-3xl'>Editor&apos;s pick</h2>
                        </div>
                        <FeaturedPostCard post={featured} />
                    </Container>
                </section>
            )}

            {/* Latest */}
            <section className='pb-16 sm:pb-20'>
                <Container>
                    <div className='mb-8 flex items-end justify-between gap-4'>
                        <h2 className='text-2xl font-bold tracking-tight sm:text-3xl'>Latest articles</h2>
                    </div>

                    <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                        {recent.map(post => (
                            <PostCard key={post.slug} post={post} />
                        ))}
                    </div>
                </Container>
            </section>

            {/* Standards */}
            <section className='border-y border-border bg-secondary/40 py-16 sm:py-20'>
                <Container>
                    <div className='max-w-2xl'>
                        <p className='text-sm font-semibold text-primary'>How we work</p>
                        <h2 className='mt-2 text-3xl font-bold tracking-tight sm:text-4xl'>
                            Health content you can rely on
                        </h2>
                    </div>

                    <div className='mt-10 grid gap-6 md:grid-cols-3'>
                        {STANDARDS.map(item => (
                            <div key={item.title} className='rounded-2xl border border-border bg-card p-6'>
                                <div className='flex size-11 items-center justify-center rounded-xl bg-primary/10 text-primary'>
                                    <item.icon className='size-5' />
                                </div>
                                <h3 className='mt-5 font-heading text-lg font-semibold tracking-tight'>
                                    {item.title}
                                </h3>
                                <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>{item.text}</p>
                            </div>
                        ))}
                    </div>

                    <div className='mt-8'>
                        <Link
                            href='/about'
                            className='inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline'
                        >
                            Read our editorial standards
                            <ArrowRight className='size-4' />
                        </Link>
                    </div>
                </Container>
            </section>

            {/* Newsletter */}
            <section className='py-16 sm:py-20'>
                <Container>
                    <Newsletter />
                </Container>
            </section>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<HomeProps> = async () => {
    const [hubs, counts, guides, guideCounts, featuredList, recentRaw, totalCount] =
        await Promise.all([
            getHubs(),
            getHubPostCounts(),
            getGuides(),
            getGuideChapterCounts(),
            getFeaturedPosts(1),
            getRecentPosts(7),
            getPostCount()
        ])

    const featured = featuredList[0] ?? null
    const recent = recentRaw.filter(post => post.slug !== featured?.slug).slice(0, 6)

    return {
        props: {
            hubs,
            counts,
            guides,
            guideCounts,
            featured,
            recent,
            totalCount
        }
    }
}

export default Home
